import type { AccordionElement, DashboardElement, TabsElement } from "../dashboard/catalog"

/** Series key used by each compatible chart type */
const SERIES_KEY_MAP: Record<string, string> = {
  BarChart: "bars",
  LineChart: "lines",
  AreaChart: "areas",
}

const COMPATIBLE_CHART_TYPES = new Set(Object.keys(SERIES_KEY_MAP))

/**
 * Transforms a tile element when its type changes, mapping series arrays
 * between compatible chart types (BarChart ↔ LineChart ↔ AreaChart).
 * For incompatible conversions, strips chart-specific fields and keeps title/type.
 */
export function transformTileType(
  element: DashboardElement,
  fromType: string,
  toType: string
): DashboardElement {
  const base = { ...element, type: toType }
  const width = (element as Record<string, unknown>).width
  const layout = width === "half" || width === "full" ? { width } : {}

  // Compatible chart-to-chart conversion: map series arrays
  if (COMPATIBLE_CHART_TYPES.has(fromType) && COMPATIBLE_CHART_TYPES.has(toType)) {
    const fromKey = SERIES_KEY_MAP[fromType]
    const toKey = SERIES_KEY_MAP[toType]
    const sourceSeries = (element as Record<string, unknown>)[fromKey]

    if (Array.isArray(sourceSeries)) {
      // Map series, keeping common fields (dataKey, color, label)
      const mapped = sourceSeries.map((s: Record<string, unknown>) => ({
        dataKey: s.dataKey,
        color: s.color,
        label: s.label,
      }))

      // Remove old series key, add new one
      const result = { ...base } as Record<string, unknown>
      delete result[fromKey]
      result[toKey] = mapped
      return result as DashboardElement
    }
  }

  // PieChart → compatible chart (Bar/Line/Area): incompatible data, strip all and keep title
  if (fromType === "PieChart" && COMPATIBLE_CHART_TYPES.has(toType)) {
    const toKey = SERIES_KEY_MAP[toType]
    return {
      type: toType,
      ...layout,
      title: (element as Record<string, unknown>).title,
      data: [],
      xKey: "category",
      [toKey]: [],
    } as unknown as DashboardElement
  }

  // Compatible chart (Bar/Line/Area) → PieChart: incompatible data, strip all and keep title
  if (COMPATIBLE_CHART_TYPES.has(fromType) && toType === "PieChart") {
    return {
      type: "PieChart",
      ...layout,
      title: (element as Record<string, unknown>).title,
      data: [],
    } as unknown as DashboardElement
  }

  // Alert requires message field
  if (toType === "Alert") {
    return {
      type: "Alert",
      ...layout,
      title: (element as Record<string, unknown>).title,
      message: "",
      variant: "info",
    } as unknown as DashboardElement
  }

  // ProgressBar uses label instead of title, requires value
  if (toType === "ProgressBar") {
    return {
      type: "ProgressBar",
      ...layout,
      label: (element as Record<string, unknown>).title,
      value: 0,
    } as unknown as DashboardElement
  }

  // Any other incompatible conversion: keep only title + type
  return {
    type: toType,
    ...layout,
    title: (element as Record<string, unknown>).title,
  } as unknown as DashboardElement
}

/**
 * Flattens the dashboard children tree into a list of leaf elements (non-layout),
 * returning references with parent info so we can modify the spec.
 */
export function getFlatTiles(children: DashboardElement[]): {
  element: DashboardElement
  parentArray: DashboardElement[]
  indexInParent: number
}[] {
  const result: {
    element: DashboardElement
    parentArray: DashboardElement[]
    indexInParent: number
  }[] = []

  for (let i = 0; i < children.length; i++) {
    const el = children[i]
    if (el.type === "Grid" || el.type === "Row") {
      const nested = el as { children: DashboardElement[] }
      result.push(...getFlatTiles(nested.children))
    } else if (el.type === "Tabs") {
      for (const tab of (el as TabsElement).tabs) {
        result.push(...getFlatTiles(tab.children))
      }
    } else if (el.type === "Accordion") {
      for (const item of (el as AccordionElement).items) {
        result.push(...getFlatTiles(item.children))
      }
    } else if (el.type === "Separator") {
      continue // Separator has no flat-index in registry
    } else {
      result.push({ element: el, parentArray: children, indexInParent: i })
    }
  }

  return result
}

/**
 * Walk the spec tree depth-first and return the element_path (dotted
 * `children[i].children[j]` form) for the tile at the given flat index.
 * Mirrors `getFlatTiles` traversal order so the index lines up with the
 * registry's flat-tile counter.
 *
 * Returns null if the index is out of range. Used by the per-tile edit
 * flow to build a JSON-Pointer-compatible path for yaml-patch.
 */
export function resolveTilePathByFlatIndex(
  spec: { children?: DashboardElement[] },
  flatIndex: number
): string | null {
  let cursor = 0
  function walk(children: DashboardElement[], prefix: string): string | null {
    for (let i = 0; i < children.length; i++) {
      const el = children[i]
      const path = `${prefix}children[${i}]`
      if (el.type === "Grid" || el.type === "Row") {
        const r = walk((el as { children: DashboardElement[] }).children, `${path}.`)
        if (r !== null) return r
      } else if (el.type === "Tabs") {
        for (let t = 0; t < (el as TabsElement).tabs.length; t++) {
          const r = walk((el as TabsElement).tabs[t].children, `${path}.tabs[${t}].`)
          if (r !== null) return r
        }
      } else if (el.type === "Accordion") {
        for (let a = 0; a < (el as AccordionElement).items.length; a++) {
          const r = walk((el as AccordionElement).items[a].children, `${path}.items[${a}].`)
          if (r !== null) return r
        }
      } else if (el.type === "Separator") {
        continue
      } else {
        if (cursor === flatIndex) return path
        cursor++
      }
    }
    return null
  }
  return walk(spec.children ?? [], "")
}

/**
 * Resolve a dotted element path back to the DashboardElement reference
 * within the given spec. Counterpart to `resolveTilePathByFlatIndex`.
 * Returns undefined if any segment doesn't resolve.
 */
export function resolveTileByPath(
  spec: { children?: DashboardElement[] },
  elementPath: string
): DashboardElement | undefined {
  // Parse "children[0].children[1]" → ["children", "0", "children", "1"]
  const parts = elementPath
    .replace(/\]/g, "")
    .split(/\.|\[/)
    .filter((p) => p !== "")
  let node: unknown = spec
  for (const p of parts) {
    if (node == null || typeof node !== "object") return undefined
    const idx = Number(p)
    if (Number.isInteger(idx)) {
      const arr = node as unknown[]
      node = arr[idx]
    } else {
      node = (node as Record<string, unknown>)[p]
    }
  }
  return node as DashboardElement | undefined
}

/**
 * Rebuilds the children array, replacing the old parent array reference with the new one.
 * This handles nested Grid/Row structures.
 */
export function rebuildChildren(
  children: DashboardElement[],
  oldParent: DashboardElement[],
  newParent: DashboardElement[]
): DashboardElement[] {
  if (children === oldParent) return newParent
  return children.map((el) => {
    if (el.type === "Grid" || el.type === "Row") {
      const nested = el as { type: string; children: DashboardElement[] }
      const rebuilt = rebuildChildren(nested.children, oldParent, newParent)
      if (rebuilt !== nested.children) {
        return { ...el, children: rebuilt } as DashboardElement
      }
    } else if (el.type === "Tabs") {
      const tabs = (el as TabsElement).tabs
      const rebuiltTabs = tabs.map((tab) => {
        const rebuilt = rebuildChildren(tab.children, oldParent, newParent)
        return rebuilt !== tab.children ? { ...tab, children: rebuilt } : tab
      })
      if (rebuiltTabs.some((tab, i) => tab !== tabs[i])) {
        return { ...el, tabs: rebuiltTabs } as DashboardElement
      }
    } else if (el.type === "Accordion") {
      const items = (el as AccordionElement).items
      const rebuiltItems = items.map((item) => {
        const rebuilt = rebuildChildren(item.children, oldParent, newParent)
        return rebuilt !== item.children ? { ...item, children: rebuilt } : item
      })
      if (rebuiltItems.some((item, i) => item !== items[i])) {
        return { ...el, items: rebuiltItems } as DashboardElement
      }
    }
    return el
  })
}
